/**
 * @file ListPagination.tsx
 * @description Футер пагинации списка (phase19.5): выбор «по сколько»,
 * счётчик «X–Y из N» и кнопки Назад/Далее.
 *
 * Переиспользуется DataTable (встроенная пагинация таблицы) и карточным
 * видом списка товаров (внешняя пагинация). Mobile-first: переносится
 * в две строки на узких экранах.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "./button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface ListPaginationProps {
  /** Всего элементов после фильтров. */
  total: number;
  /** Текущая страница (0-based). */
  pageIndex: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
  pageSizeOptions?: number[];
}

export function ListPagination({
  total,
  pageIndex,
  pageSize,
  onPageSizeChange,
  onPrev,
  onNext,
  canPrev,
  canNext,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: ListPaginationProps) {
  const from = total === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(total, (pageIndex + 1) * pageSize);

  return (
    <div className="flex items-center justify-between gap-x-4 gap-y-2 flex-wrap">
      <div className="flex items-center gap-2 text-sm text-on-surface-variant">
        <span className="whitespace-nowrap">Показывать по</span>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => onPageSizeChange(Number(v))}
        >
          <SelectTrigger className="h-8 w-[76px]" aria-label="Товаров на странице">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-on-surface-variant tabular-nums whitespace-nowrap">
          {from}–{to} из {total}
        </span>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={onPrev}
            disabled={!canPrev}
            aria-label="Предыдущая страница"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={onNext}
            disabled={!canNext}
            aria-label="Следующая страница"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
